// src/hooks/useDirectoryListings.js
//
// Shared directory-data hook for the Locality directory surface. Lifted out of
// SidebarLocality (June 2026, Phase 2) so the desktop Directory Mode Column and the
// mobile sidebar fetch the same data the same way — one source of truth.
//
// Resolves the active base freguesia (primary_boundary_id), then loads directory listings +
// the locality hero image. Also derives Steward/admin review permission and the pending-suggestion count.
// Components own only their own modal open/close UI state.
//
// Listings show wherever they apply (Roadmap #87, R1–R2): all three sections carry the area's
// own listings, every wider area's, and the national ones for its country, local first, from one
// read (getDirectoryListingsWithAncestors → get_directory_listings). Official Contacts arrive
// merged and authority-ordered. Each row keeps area_name / distance / is_national for its area
// label; this hook does no grouping or sorting of its own beyond the utility categories.

import { useState, useEffect, useCallback } from 'react';
import { getDirectoryListingsWithAncestors, getLocalityHeroImage, getReviewableSuggestions } from '../data/directoryService';
import { isPlatformAdmin, isSteward } from '../utils/adminHelpers';
import { baseParishId } from '../utils/scopeResolution';

// The fields a row's area label needs (Roadmap #87, R2): a wider listing names its area, a
// national one says so; the area's own listings carry no label.
const areaFields = (r) => ({ areaName: r.area_name || null, distance: r.distance ?? null, isNational: Boolean(r.is_national) });

// Map directory_listings rows into the shape ContactList expects
function mapOfficialContacts(rows) {
  const iconMap = {
    junta: '\u{1F3DB}️',       // 🏛️
    camara: '\u{1F3E2}',             // 🏢
    health_centre: '\u{1F3E5}'       // 🏥
  };
  return rows.map(r => ({
    id: r.id,
    name: r.name,
    phone: r.phone || '',
    icon: iconMap[r.category] || '\u{1F3DB}️',
    languages: r.description || '',
    groupId: r.claim_status === 'verified' ? r.claimed_by_group_id : null,
    // Carried so a "Suggest a correction" opens the shared modal with the right section chip
    // (the contact's own section, not the default 'directory') and a faithful pre-fill. Data
    // still writes to the correct row by listing_id regardless — this is purely modal display.
    section: r.section,
    category: r.category,
    description: r.description || '',
    email: r.email || '',
    website: r.website || '',
    address: r.address || '',
    tags: r.tags || [],
    ...areaFields(r)
  }));
}

// Map utility rows into the UtilityCategoryList shape (grouped by category, with derived counts)
function mapUtilityCategories(rows) {
  const iconMap = {
    electricity: '⚡',   // ⚡
    water: '\u{1F4A7}',      // 💧
    gas: '\u{1F525}',        // 🔥
    internet_phone: '\u{1F4E1}' // 📡
  };
  const keyMap = {
    electricity: 'utility_electricity',
    water: 'utility_water',
    gas: 'utility_gas',
    internet_phone: 'utility_internet'
  };
  const grouped = {};
  rows.forEach(r => {
    const cat = r.category || 'other';
    if (!grouped[cat]) {
      grouped[cat] = {
        id: cat,
        icon: iconMap[cat] || '\u{1F4DE}',
        translationKey: keyMap[cat] || `utility_${cat}`,
        providers: []
      };
    }
    grouped[cat].providers.push({
      id: r.id,
      name: r.name,
      phone: r.phone || '',
      languages: r.description || '',
      // Carried so a "Suggest a correction" opens the shared modal with the right section chip
      // ('utility', not the default 'directory') and a faithful pre-fill. Data still writes to
      // the correct row by listing_id regardless of these — this is purely modal display.
      section: r.section,
      category: r.category,
      description: r.description || '',
      email: r.email || '',
      website: r.website || '',
      address: r.address || '',
      tags: r.tags || [],
      ...areaFields(r)
    });
  });
  // Stable order: electricity, water, gas, internet_phone, then any others
  const order = ['electricity', 'water', 'gas', 'internet_phone'];
  const sorted = order.filter(k => grouped[k]).map(k => grouped[k]);
  Object.keys(grouped).forEach(k => {
    if (!order.includes(k)) sorted.push(grouped[k]);
  });
  return sorted;
}

export function useDirectoryListings({ selectedArea = null, currentUser = null, guestFallbackBoundaryId = null, visitBoundaryId = null } = {}) {
  // Directory data state. Starts empty — an unseeded locality must render as empty, not
  // as fabricated contacts (see docs/briefs/2026-08-09-mock-contact-fallback-fix.md).
  const [contacts, setContacts] = useState([]);
  const [utilities, setUtilities] = useState([]);
  const [directoryListings, setDirectoryListings] = useState([]);
  const [heroImageUrl, setHeroImageUrl] = useState(null);
  const [heroImageCredit, setHeroImageCredit] = useState(null);
  const [pendingSuggestions, setPendingSuggestions] = useState(0);
  const [isStewardForBoundary, setIsStewardForBoundary] = useState(false);
  const [directoryScopeIds, setDirectoryScopeIds] = useState([]);

  // An active temporary visit (Change 3) wins over everything else — Directory is a feed view,
  // so it must show the visited locality, never selectedArea/home (addendum-1 §C). Otherwise, the
  // member's own parish (baseParishId: the base parish, else the FIRST saved boundary — this hook
  // used to take the last, so it could name a different parish from the header). Guests have no
  // selectedArea (no user_areas), so fall back to the boundary App already resolved for the feed —
  // without it the directory never fetches for logged-out visitors (headers render but no
  // counts/contents).
  const boundaryId = visitBoundaryId
    || baseParishId(selectedArea)
    || guestFallbackBoundaryId;

  const isAdmin = isPlatformAdmin(currentUser);

  // Fetch directory data when boundary changes
  useEffect(() => {
    if (!boundaryId) return;

    let cancelled = false;

    const fetchData = async () => {
      const scopeIds = [boundaryId];

      if (cancelled) return;
      setDirectoryScopeIds(scopeIds);

      const [listings, heroData] = await Promise.all([
        getDirectoryListingsWithAncestors(scopeIds, boundaryId),
        getLocalityHeroImage(boundaryId)
      ]);

      if (cancelled) return;

      // Always reflect the real result, including empty — an empty result must render as
      // empty, not silently keep whatever was there before.
      setContacts(mapOfficialContacts(listings.officialContacts));
      setUtilities(mapUtilityCategories(listings.utilities));
      setDirectoryListings(listings.directory.map(r => ({ ...r, ...areaFields(r) })));

      if (heroData.hero_image_url) {
        setHeroImageUrl(heroData.hero_image_url);
        setHeroImageCredit(heroData.hero_image_credit);
      }
    };

    fetchData();

    return () => { cancelled = true; };
  }, [boundaryId]);

  // Check Steward status for the current boundary
  useEffect(() => {
    if (!boundaryId || !currentUser?.id || isAdmin) {
      setIsStewardForBoundary(false);
      return;
    }
    let cancelled = false;
    isSteward(currentUser.id, boundaryId).then(result => {
      if (!cancelled) setIsStewardForBoundary(result);
    });
    return () => { cancelled = true; };
  }, [boundaryId, currentUser?.id, isAdmin]);

  const canReviewSuggestions = isAdmin || isStewardForBoundary;

  // Pending suggestions this person may decide, wherever they were filed (Roadmap #87, R6) — the
  // same source as the review queue, so the badge and the queue always agree.
  useEffect(() => {
    if (directoryScopeIds.length === 0 || !canReviewSuggestions) {
      setPendingSuggestions(0);
      return;
    }
    getReviewableSuggestions().then(rows => setPendingSuggestions(rows.length));
  }, [directoryScopeIds, canReviewSuggestions]);

  // Re-fetch directory listings when a suggestion is approved
  useEffect(() => {
    if (directoryScopeIds.length === 0) return;

    const handleDirectoryChanged = async () => {
      const listings = await getDirectoryListingsWithAncestors(directoryScopeIds, boundaryId);
      setContacts(mapOfficialContacts(listings.officialContacts));
      setUtilities(mapUtilityCategories(listings.utilities));
      setDirectoryListings(listings.directory.map(r => ({ ...r, ...areaFields(r) })));
    };

    window.addEventListener('ournet:directory-changed', handleDirectoryChanged);
    return () => window.removeEventListener('ournet:directory-changed', handleDirectoryChanged);
  }, [directoryScopeIds, boundaryId]);

  // Imperative refresh of the pending-suggestion count (after a suggestion is submitted)
  const refreshPending = useCallback(() => {
    if (directoryScopeIds.length === 0 || !canReviewSuggestions) return;
    getReviewableSuggestions().then(rows => setPendingSuggestions(rows.length));
  }, [directoryScopeIds, canReviewSuggestions]);

  return {
    boundaryId,
    contacts,
    utilities,
    directoryListings,
    heroImageUrl,
    heroImageCredit,
    canReviewSuggestions,
    pendingSuggestions,
    setPendingSuggestions,
    refreshPending
  };
}

export default useDirectoryListings;
